import { jsx as _jsx } from "react/jsx-runtime";
import cx from "classnames";
import { forwardRef } from "react";
import cn from "variant-classnames";
import { toolkitCx, toolkitTestIds } from "../../../constants";
const variants = {
    $all: "",
    size: {
        "2xs": "text-xxs",
        xs: "text-xs",
        sm: "text-sm",
        base: "text-base",
        md: "text-base",
        lg: "text-lg",
        xl: "text-xl",
        "2xl": "text-2xl",
        "3xl": "text-3xl",
        "4xl": "text-4xl",
        "5xl": "text-5xl",
        "6xl": "text-6xl",
        "7xl": "text-7xl",
    },
    variant: {
        current: toolkitCx.layout.text.current,
        error: toolkitCx.layout.text.error,
        forceDark: toolkitCx.layout.text.dark,
        forceLight: toolkitCx.layout.text.light,
        info: toolkitCx.layout.text.info,
        muted: toolkitCx.layout.text.muted,
        normal: toolkitCx.layout.text.default,
        primary: toolkitCx.layout.text.primary,
        silent: toolkitCx.layout.text.silent,
        success: toolkitCx.layout.text.success,
        warning: toolkitCx.layout.text.warning,
        secondary: toolkitCx.layout.text.secondary,
    },
    weight: {
        thin: "font-thin",
        extralight: "font-extralight",
        light: "font-light",
        normal: "font-normal",
        medium: "font-medium",
        semibold: "font-semibold",
        bold: "font-bold",
        extrabold: "font-extrabold",
        black: "font-black",
    },
};
/**
 * The Text component is a versatile and customizable text element that can be used to display text with various
 * font sizes, font weights, and color variants. It supports using different HTML tags for the text element
 * and allows customization with gradient and hover effects.
 *
 * @component
 * @param {ITextProps} props - The props for the Text component.
 * @param {React.ReactNode} props.children - The content to be displayed within the Text component.
 * @param {boolean} [props.gradient] - If true, applies a gradient color to the text. Default is false.
 * @param {boolean} [props.hoverable] - If true, applies hover effect to the text. Default is false.
 * @param {string} [props.size] - The font size of the text. One of "2xs", "xs", "sm", "base", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl", "7xl". Default is "base".
 * @param {React.ElementType} props.tag - The HTML tag used for the text element.
 * @param {string} [props.testId="rich-text"] - The test id attribute for the Text component.
 * @param {string} [props.variant] - The color variant of the text. One of "silent", "muted", "normal", "primary", "forceLight", "forceDark", "error", "info", "warning", "success". Default is "normal".
 * @param {string} [props.weight] - The font weight of the text. One of "thin", "extralight", "light", "normal", "medium", "semibold", "bold", "extrabold", "black". Default is "normal".
 * @param {string} [props.className] - Additional CSS class to apply to the Text component.
 * @param {React.HTMLAttributes<HTMLElement>} textProps - The additional HTML attributes for the text element.
 * @param {React.Ref<HTMLElement>} forwardedRef - A ref that can be used to access the underlying HTMLElement of the Text component.
 * @returns {JSX.Element} A rendered Text component.
 */
const Text = ({ children, gradient, hoverable, size = "base", tag: Tag, testId = toolkitTestIds.text, variant = "normal", weight = "normal", className, ...textProps }, forwardedRef) => {
    const textVariantCn = cn(variants, {
        className,
        size,
        variant,
        weight,
    });
    return (_jsx(Tag, { ...textProps, className: cx(textVariantCn, toolkitCx.layout.transition.colors, {
            "hover:text-primary-200/70 dark:hover:text-primary-200/70": variant === "primary",
            "hover:text-black/70 hover:dark:text-gray-200/70": hoverable && variant === "normal",
            "hover:text-black/40 hover:dark:text-gray-200/40": hoverable && variant === "muted",
            "hover:text-black/20 hover:dark:text-gray-200/20": hoverable && variant === "silent",
        }, {
            "text-fill-color-transparent bg-gradient-to-r from-secondary to-primary bg-[length:100%_100%] bg-clip-text": gradient,
        }, {
            "hover:from-secondary-300 hover:to-primary-300": gradient && hoverable,
        }), "data-testid": testId, ref: forwardedRef, children: children }));
};
export default forwardRef(Text);
//# sourceMappingURL=Text.js.map